import type { BucketKey, OrderBucketKey } from './types';

/**
 * Pastel palette. One colour per bucket, used the same way everywhere: chips, table
 * accents, charts. Text is always a dark shade of the same hue so it stays readable.
 * Tailwind needs full class names in the source, so every class below is written out.
 */
export interface BucketMeta {
  key: OrderBucketKey;
  label: string;
  /** What one "received" means for this bucket -- stated on screen so numbers are not misread. */
  unit: string;
  chip: string;      // pill: background + text + border
  dot: string;       // small solid dot
  tint: string;      // soft row / header wash
  hex: string;       // chart fill
}

export const BUCKETS: Record<OrderBucketKey, BucketMeta> = {
  abc: {
    key: 'abc', label: 'ABC', unit: 'ABC work items (one per cart event)',
    chip: 'bg-violet-100 text-violet-800 border-violet-200 dark:bg-violet-400/15 dark:text-violet-200 dark:border-violet-400/30',
    dot: 'bg-violet-300', tint: 'bg-violet-50/70 dark:bg-violet-400/5', hex: '#c4b5fd',
  },
  reorder: {
    key: 'reorder', label: 'Reorder', unit: 'Reorder work items (one per product due date)',
    chip: 'bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-400/15 dark:text-orange-200 dark:border-orange-400/30',
    dot: 'bg-orange-300', tint: 'bg-orange-50/70 dark:bg-orange-400/5', hex: '#fdc9a0',
  },
  rto: {
    key: 'rto', label: 'RTO', unit: 'RTO work items (one per order)',
    chip: 'bg-rose-100 text-rose-800 border-rose-200 dark:bg-rose-400/15 dark:text-rose-200 dark:border-rose-400/30',
    dot: 'bg-rose-300', tint: 'bg-rose-50/70 dark:bg-rose-400/5', hex: '#fda4af',
  },
  edd: {
    key: 'edd', label: 'EDD', unit: 'EDD work items (one per order)',
    chip: 'bg-sky-100 text-sky-800 border-sky-200 dark:bg-sky-400/15 dark:text-sky-200 dark:border-sky-400/30',
    dot: 'bg-sky-300', tint: 'bg-sky-50/70 dark:bg-sky-400/5', hex: '#a5d8f3',
  },
  ndr: {
    key: 'ndr', label: 'NDR', unit: 'NDR work items (one per order)',
    chip: 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-400/15 dark:text-amber-200 dark:border-amber-400/30',
    dot: 'bg-amber-300', tint: 'bg-amber-50/70 dark:bg-amber-400/5', hex: '#fde68a',
  },
  agent_lead: {
    key: 'agent_lead', label: 'Agent Lead', unit: 'Agent-created leads (one work item per lead)',
    chip: 'bg-fuchsia-100 text-fuchsia-800 border-fuchsia-200 dark:bg-fuchsia-400/15 dark:text-fuchsia-200 dark:border-fuchsia-400/30',
    dot: 'bg-fuchsia-300', tint: 'bg-fuchsia-50/70 dark:bg-fuchsia-400/5', hex: '#eab8f5',
  },
  phone: {
    key: 'phone', label: 'Phone', unit: 'Phone work items (one per lead)',
    chip: 'bg-teal-100 text-teal-800 border-teal-200 dark:bg-teal-400/15 dark:text-teal-200 dark:border-teal-400/30',
    dot: 'bg-teal-300', tint: 'bg-teal-50/70 dark:bg-teal-400/5', hex: '#99e6dc',
  },
  none: {
    key: 'none', label: 'No open work', unit: 'Orders with no bucket tag and no open work for the customer at the time of the order',
    chip: 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-400/15 dark:text-slate-200 dark:border-slate-400/30',
    dot: 'bg-slate-300', tint: 'bg-slate-50 dark:bg-slate-400/5', hex: '#cbd5e1',
  },
};

export const WORK_BUCKETS: BucketKey[] = ['abc', 'reorder', 'rto', 'edd', 'ndr', 'agent_lead', 'phone'];

export type Tone = 'mint' | 'lavender' | 'peach' | 'sky' | 'butter' | 'rose' | 'slate';

/** Soft card washes for the KPI tiles. */
export const TONES: Record<Tone, { card: string; icon: string; grad: string; watermark: string }> = {
  mint:     { card: 'bg-emerald-50 border-emerald-100 dark:bg-emerald-400/10 dark:border-emerald-400/20', icon: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-400/20 dark:text-emerald-200', grad: 'from-emerald-50 via-white to-white dark:from-emerald-400/10 dark:via-slate-800 dark:to-slate-800', watermark: 'text-emerald-400 dark:text-emerald-300' },
  lavender: { card: 'bg-violet-50 border-violet-100 dark:bg-violet-400/10 dark:border-violet-400/20',     icon: 'bg-violet-100 text-violet-700 dark:bg-violet-400/20 dark:text-violet-200', grad: 'from-violet-50 via-white to-white dark:from-violet-400/10 dark:via-slate-800 dark:to-slate-800', watermark: 'text-violet-400 dark:text-violet-300' },
  peach:    { card: 'bg-orange-50 border-orange-100 dark:bg-orange-400/10 dark:border-orange-400/20',     icon: 'bg-orange-100 text-orange-700 dark:bg-orange-400/20 dark:text-orange-200', grad: 'from-orange-50 via-white to-white dark:from-orange-400/10 dark:via-slate-800 dark:to-slate-800', watermark: 'text-orange-400 dark:text-orange-300' },
  sky:      { card: 'bg-sky-50 border-sky-100 dark:bg-sky-400/10 dark:border-sky-400/20',                 icon: 'bg-sky-100 text-sky-700 dark:bg-sky-400/20 dark:text-sky-200', grad: 'from-sky-50 via-white to-white dark:from-sky-400/10 dark:via-slate-800 dark:to-slate-800', watermark: 'text-sky-400 dark:text-sky-300' },
  butter:   { card: 'bg-amber-50 border-amber-100 dark:bg-amber-400/10 dark:border-amber-400/20',         icon: 'bg-amber-100 text-amber-700 dark:bg-amber-400/20 dark:text-amber-200', grad: 'from-amber-50 via-white to-white dark:from-amber-400/10 dark:via-slate-800 dark:to-slate-800', watermark: 'text-amber-400 dark:text-amber-300' },
  rose:     { card: 'bg-rose-50 border-rose-100 dark:bg-rose-400/10 dark:border-rose-400/20',             icon: 'bg-rose-100 text-rose-700 dark:bg-rose-400/20 dark:text-rose-200', grad: 'from-rose-50 via-white to-white dark:from-rose-400/10 dark:via-slate-800 dark:to-slate-800', watermark: 'text-rose-400 dark:text-rose-300' },
  slate:    { card: 'bg-slate-50 border-slate-200 dark:bg-slate-400/10 dark:border-slate-400/20',         icon: 'bg-slate-100 text-slate-600 dark:bg-slate-400/20 dark:text-slate-200', grad: 'from-slate-50 via-white to-white dark:from-slate-400/10 dark:via-slate-800 dark:to-slate-800', watermark: 'text-slate-400 dark:text-slate-300' },
};

/** Chart colours that are not buckets. */
export const CHART = {
  worked: '#a7e3c8',     // mint
  received: '#c7d2fe',   // periwinkle
  cancelled: '#fecdd3',  // rose
  revenue: '#bfdbfe',    // pale blue
  grid: '#e5e7eb',
};
